<template>
<div :class="'' + getCalendarClasses()">
    <div class="calendar-input">
        <button
            class="calendar-input__button"
            :aria-expanded="$store.state.showCalendar ? 'true' : 'false'"
            aria-controls="ac-calendar-window"
            @click="toggleWindow('calendar')"
            v-click-outside="hideWindow"
        >
            <span class="calendar-input__left">{{$store.state.calendarTextCheckIn}}</span>
            <span class="calendar-input__right">{{$store.state.calendarTextCheckOut}}</span>
        </button>
        <div class="calendar-indicator"></div>
    </div>
    <div class="popup-container">
        <div id="ac-calendar-window" class="popup-window calendar-window" v-show="$store.state.showCalendar">
            <div v-show="$store.state.isLoading" class="calendar-loader">
                <div class="icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50"><path d="M37.4 28.9c.9-2.9.9-5.6-.2-8.4-1.1-2.6-2.7-4.7-5.1-6.1-3.6-2.1-7.4-2.5-11.2-1-3 1.1-5.3 3.2-6.6 6.2-.6 1.3-1 2.6-1.2 4-.2 1.6-1 3-2.6 3.6-2.3.9-5.4-.8-5.6-3.7-.1-1.4.2-2.7.5-3.9 1.4-5.1 4.4-9.1 8.7-12.1C17.8 5 22 3.9 26.5 4.3c2.2.2 4.2.6 6.3 1.4 4.3 1.6 7.6 4.4 10.2 8.2 2.7 4.1 3.6 8.6 3.2 13.4-.3 2.4-.9 4.7-2 6.8-1.5 3.1-3.6 5.7-6.5 7.7-3.6 2.6-7.7 3.9-12.1 4.1-2.5 0-5-.5-7.4-1.4-2.1-.8-3.9-1.8-5.6-3.2-1.5-1.2-2.1-3.1-1.7-4.9.6-1.7 2.4-2.8 4.3-2.6.8.1 1.6.4 2.2.9 1.4 1.1 3 1.9 4.7 2.4 2.1.6 4.3.7 6.5 0 3.6-1 6.2-3 7.9-6.4.2-.3.8-1.4.9-1.8z" fill="#3f3f3f"/></svg>
                </div>
            </div>
            <p class="calendar-title">{{$store.state.calendarTitle}}</p>

            <div class="calendar">
                <v-date-picker
                    mode='range'
                    :value="[]"
                    :attributes="$store.state.pickerAttributes"
                    :min-date="minDate"
                    :max-date="maxDate"
                    :available-dates="$store.state.apiDates.place.availableCheckInDates"
                    :disabled-dates="$store.state.apiDates.place.disabledDates"
                    :select-attribute="selectAttr"
                    :drag-attribute="dragAttr"
                    @dayclick="onDayClick($event)"
                    is-linked
                    is-inline
                    :isDoublePaned="$store.state.calendarHasDoublePane"
                    :is-expanded="!$store.state.calendarHasDoublePane"
                    @update:topage="paginate($event)"
                    @update:frompage="paginate($event)"
                    v-if="!$store.state.forceReload"
                ></v-date-picker>
            </div>

            <div class="calendar-key calendar-key--large">
                <ul>
                    <li class="checkinoutday" v-show="$store.state.showKeyCheckInOut"><span>{{$store.state.keyCheckInOutText}}</span></li>
                    <li class="onrequest" v-show="$store.state.showKeyOnRequest"><span>On request</span></li>
                    <li class="booked"><span>Not available</span></li>
                </ul>
            </div>

            <div class="calendar-divider"></div>

            <p class="calendar-nights">{{$store.state.calendarText}}<b> {{$store.state.totalNightsSelected}}</b></p>

            <div class="calendar-window__actions">
                <button class="button calendar-clear" @click="clearSelectedDates">Clear</button>
                <button class="button" @click="applySelectedDates" :disabled="$store.state.dateSelectionComplete !== true">Apply</button>
            </div>
        </div>
    </div>
</div>
</template>

<script>

/* Import moment directly to stop loading ALL locales
 * See: https://stackoverflow.com/questions/25384360/how-to-prevent-moment-js-from-loading-locales-with-webpack
 */
import moment from "moment/src/moment";

import ClickOutside from 'vue-click-outside';
import { getCookie } from "../utils/cookie.ts";

export default {
    name: 'GuestDateSelector',
    directives: {
        ClickOutside
    },
    data() {
        return {
            dragAttr: {
                highlight: {
                    backgroundColor: '#35A29A',
                    borderRadius: "0",
                    width: "100%"
                },
                contentStyle: {
                    backgroundColor: '#35A29A',
                    color: '#FFFFFF'
                },
                key: 3
            },
            selectAttr: {
                highlight: {
                    backgroundColor: '#35A29A',
                    borderRadius: "0",
                    width: "100%"
                },
                contentStyle: {
                    backgroundColor: '#35A29A',
                    color: '#FFFFFF'
                },
                key: 3
            }
        }
    },
    computed: {
        minDate() {
            return new Date(this.$store.state.apiDates.minMaxDates.start);
        },
        maxDate() {
            return new Date(this.$store.state.apiDates.minMaxDates.end);
        }
    },
    mounted() {
        let self = this;

        window.addEventListener('keyup', function(event) {
            if (event.keyCode === 27) { // ESC key
                if (self.$store.state.showCalendar == true) {
                    self.toggleWindow('calendar');
                }
            }
        });

        // On places with live availability we can only parse the cookie after
        // calls to the API have finished - otherwise the calendar assumes the
        // dates are unavailable.
        const cookie = getCookie("search_cookie__availability");

        if (cookie && cookie.availability) {
            this.$store.dispatch('calendarSetup', () => {
                this.parseCookie(cookie);
            });
        }

        this.popupItem = this.$el;

        this.hideInactiveKeys();
    },
    methods: {
        parseCookie: function(cookie) {
            if ( typeof cookie.availability.checkin === "string"
                && typeof cookie.availability.checkout === "string"
            ) {
                const checkin = {
                    date: moment(cookie.availability.checkin).format('YYYY-MM-DD'),
                    inMonth: true
                };

                const checkout = {
                    date: moment(cookie.availability.checkout).format('YYYY-MM-DD'),
                    inMonth: true
                };

                this.$store.dispatch('dateSelectionController', checkin);
                this.$store.dispatch('dateSelectionController', checkout);
            }
        },
        onDayClick: function(event) {
            let dateInfo = {
                date: moment(event.date).format('YYYY-MM-DD'),
                inMonth: event.inMonth
            }

            // This can be used to address the calendar compent directly
            // this.$children[0].$children[0].$children[0].$children[0].moveNextMonth()

            this.$store.dispatch('dateSelectionController', dateInfo);
        },
        toggleWindow: function() {
            if (this.$store.state.requirements.totalGuests > 0) {
                this.$store.dispatch('toggleWindow', 'calendar');

                if (this.$store.state.selectedDate.length === 0) {
                    this.$store.dispatch('hideErrorMessage');
                    this.$store.dispatch('calendarSetup');
                }
            } else {
                this.$store.dispatch('showErrorMessage');
            }
        },
        hideWindow: function(event) {
            // EDGE hack to stop close window requests on calendar pagination
            if( event.target.tagName != 'svg' && event.target.tagName != 'path' ){
                if(this.$store.state.showCalendar){
                    this.$store.dispatch('closedCalendar');
                }
            }
        },
        hideInactiveKeys: function() {
            this.$store.dispatch('hideKeys');
        },
        clearSelectedDates: function() {
            const guests = this.$store.state.guestSelector.guestInputValue;

            this.$store.dispatch('updateAllGuestInputValues', guests);
            this.$store.dispatch('calendarSetup');
        },

        /**
         * Has check-in date available
         *
         * @return {boolean} Checkin date?
         */
        hasCheckin: function() {
            return ( this.$store.state.selectedDate.length > 0 );
        },

        /**
         * Has check-out date available
         *
         * @return {boolean} Checkout date?
         */
        hasCheckout: function() {
            return ( this.$store.state.selectedDate.length > 1 );
        },

        getCalendarClasses: function() {
            let classes = '';

            if (this.$store.state.showCalendar) {
                classes += ' calendar-is-open';
            }

            if (this.hasCheckin()) {
                classes += ' has-checkin';
            }

            if (this.hasCheckout()) {
                classes += ' has-checkout';
            }

            return classes;
        },
        applySelectedDates: function() {
            this.$store.dispatch('closedCalendar');
            this.$store.dispatch('updateGuestsDatesSummary');
        },
        paginate(payload){
            if( this.$store.state.showCalendar == true ){

                if (payload !== undefined){

                    if (payload.position !== undefined && this.$store.state.place.availability_cal != 0){

                        //TODO this is repeated code from src/components/BookableItem.vue.. GET THIS INTO store.js

                        // console.log("payload.key")
                        // console.log(payload.key)
                        // console.log("this.$store.state.calendarPaginationKey")
                        // console.log(this.$store.state.calendarPaginationKey)

                        // Check to see if the user is paginating forward
                        if( payload.key > this.$store.state.calendarPaginationKey ){

                            // console.log("currentPaginationKey");
                            // console.log(this.$store.state.calendarPaginationKey);
                            // console.log("payload.key");
                            // console.log(payload.key);

                            // console.log('paginated forwards');

                            let endMonthSanitized = payload.monthComps.month;

                            if(payload.monthComps.month < 10){
                                endMonthSanitized = "0" + payload.monthComps.month
                            }

                            var endDate = new Date(payload.monthComps.year + "-" + endMonthSanitized + "-" + payload.monthComps.days);
                            let endDatePlusAMonth = moment(endDate).add(2,'months').format("YYYY-MM-DD");

                            if( this.$store.state.apiDates.minMaxDates.end < endDatePlusAMonth ){
                                endDatePlusAMonth = this.$store.state.apiDates.minMaxDates.end;
                            }

                            let prevMonthSanitized = payload.prevMonthComps.month;

                            // This annoying hack to add a '0' to month numbers is due to moment.js falling over when trying to use format("YYYY-M-DD")
                            // or format("YYYY-M-D") but works fine with format("YYYY-MM-DD")
                            if(payload.prevMonthComps.month < 10){
                                prevMonthSanitized = "0" + payload.prevMonthComps.month
                            }

                            // We use the last day of the previous month to work out if the 1st of the month is a viable check out day.
                            // a value for '.prev' on the bookable day doesn't exist if we query the 1st
                            let modified_start_date = moment(payload.prevMonthComps.year + "-" + prevMonthSanitized + "-01").subtract('1', 'day').format("YYYY-MM-DD");

                            let dates = {
                                start: modified_start_date,
                                end: endDatePlusAMonth
                            }

                            this.$store.dispatch('getApiResponse', dates);

                        }

                        //TODO Create a setter for this
                        this.$store.state.calendarPaginationKey = payload.key

                    }
                }
            }
        }
    }
}
</script>

<style lang="scss">
    @import "../assets/styles/toolbox.scss";

    .calendar-window {
        .c-day-content {
            @include media(max-width, $bp--app-fixed - 1px) {
                font-size: rem(18px) !important;
            }
        }
    }
</style>

<style scoped lang="scss">
    @import "../assets/styles/toolbox.scss";

    .calendar-input {
        position: relative;

        &__button {
            display: flex;
            border: 1px solid #ccc;
            border-radius: rem(10px);
            font-size: rem(18px);
            line-height: 1.2;
            padding: 0 rem(10px);
            width: 100%;
            // Explicitly set to override IOS/Safari default styles for buttons
            // See: https://developer.apple.com/forums/thread/690529
            color: #757575;

            @media (min-width: $bp--app-fixed) {
                padding: 0;
            }
        }

        // "Check In" / "Check Out" labels
        &__left, &__right {
            flex: 1 0 1px;
            padding: rem(12px) 0;
        }

        &__left {
            border-right: 1px solid #ccc;
        }

        &__right {
            border-left: 1px solid #ccc;
        }
    }

    .has-checkin .calendar-input__left {
        color: #000;
    }

    .has-checkout .calendar-input__right {
        color: #000;
    }

    .calendar-indicator {
        height: rem(5px);
        background-color: #2EA39B;
        width: 44%;
        position: absolute;
        bottom: 0;
        left: 3%;
        z-index: 5;
        display: none;

        @include media(min-width, $bp--app-fixed) {
            left: 4%;
            width: 43%;
            bottom: rem(1px);
        }

        .calendar-is-open & {
            display: block;
        }

        .has-checkin & {
            right: 3%;
            left: auto;

            @include media(min-width, $bp--app-fixed) {
                right: 4%;
                width: 38%;
            }
        }

        .has-checkin.has-checkout & {
            display: none;
        }
    }

    input[type="text"], .calendar-input__button {
        cursor: pointer;
    }

    .popup-container {
        position: relative;
    }

    .popup-window {
        background-color: white;
        padding: rem(20px) rem(8px) rem(8px);
        right: 0;

        @include media(min-width, 368px) {
            padding: rem(20px) rem(16px) rem(16px);
        }

        @include media(min-width, $bp--app-fixed) {
            padding: rem(18px);
        }
    }

    .calendar-window {
        @include media(max-width, $bp--app-fixed - 1px) {
            left: rem(1px);
            right: rem(1px);
        }

        @include media(min-width, $bp--app-fixed) {
            position: absolute;
            top: -16rem;
            width: rem(580px);
            box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1),
                        0 0 0 1px rgba(10, 10, 10, 0.1);
            z-index: 5;
        }
    }


    @keyframes spin {
        from {
            transform:rotate(-0deg);
        }
        to {
            transform:rotate(-360deg);
        }
    }

    .calendar-loader {
        position: absolute;
        z-index: 5;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        background: rgba(#cccccc, 0.6);
        display: flex;
        justify-content: center;
        align-items: center;

        .icon {
            width: rem(66px);
            height: rem(66px);
            transform-origin: center;
            animation-name: spin;
            animation-duration: 800ms;
            animation-iteration-count: infinite;
            animation-timing-function: linear;
        }
    }

    .calendar {
        max-width: rem(400px);
        margin: 0 auto;

        @include media(min-width, $bp--app-fixed) {
            max-width: none;
        }
    }

    .button {
        font-size: rem(17px);
        border-radius: 6px;
        padding: rem(9px);

        &.hollow {
            background: transparent;
        }

        &[disabled="disabled"] {
            background-color: red;
            border-color: #B8B8B8;
        }


        @include media(min-width, $bp--app-fixed) {
            font-size: rem(12px);
            letter-spacing: 0.32px;
            line-height: 16px;
            border-radius: 7px;
            padding: rem(8px);
        }
    }

    .calendar-window__actions {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;

        button {
            margin: rem(16px) rem(8px);
            flex: 1 0 rem(80px);
        }

        .calendar-clear {
            color: #585858;
            border: none;
            border-bottom: 2px solid #585858;
            border-radius: 0;
            background-color: transparent;

            &:hover,
            &:focus {
                border-bottom-color: #e5aa26;
                background-color: transparent;
            }
        }
    }


    .calendar-title,
    .calendar-nights {
        font-size: rem(18px) !important;
        margin: 0;
        color: #252525;
        text-align: center;

        @include media(min-width, $bp--app-fixed) {
            font-size: rem(16px) !important;
        }
    }

    .calendar-title {
        font-weight: bold;
    }

    .calendar-nights {
        margin: rem(20px) 0 rem(17px);

        @include media(min-width, $bp--app-fixed) {
            margin: rem(13px) 0 rem(10px);
        }
    }

    .calendar--close {
        float: right;
        font-weight: bold;
        cursor: pointer;
    }

    .close-row {
        margin-bottom: 10px;
    }

    .calendar-key {
        margin-top: rem(15px);
    }

    .calendar-key--large {
        li {
            width: 50%;
            margin: 0 0 rem(10px);

            @include media(min-width, 320px) {
                margin: 0 rem(5px) 0 0;
                width: auto;
            }

            @include media(min-width, 420px) {
                margin: 0 rem(13px) 0 0;
            }
        }

        ul {
            width: 100%;
            max-width: rem(400px);
            margin: 0 rem(5px);
            justify-content: flex-start;

            @include media(min-width, 320px) {
                justify-content: space-between;
            }

            @include media(min-width, $bp--app-fixed) {
                justify-content: center;
            }
        }
    }
</style>
